// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import SlideInOverlay from '@ta/overlays.slide-in';
import { Opener } from '@ta/overlays.managers';
import { Icon } from '@ta/styleguide.icon';
import classNames from '@ta/common.classnames';
import trackEventAJAX, { ClickTracker, type TrackingEventAJAXProps } from '@ta/common.tracking';
import PostButtonCollapsedMenu, {
  MobileMediaActionsBlock,
  showLinkUploaderFromVideoUploader,
  setUploaderModal,
} from '@ta/brand.mobile-global-nav-content-collect';
import { localize } from '@ta/common.i18n';
import { VisibleLinks, SideBarNavLinksBinding } from '@ta/brand.global-nav';
import { featureIsEnabled } from '@ta/common.features';
import type { NavLinkType } from '@ta/brand.global-nav';
import { useExplicitPreferencesModal } from '@ta/onboarding.explicit-preferences-entry';
import useExplicitPreferencesPreLoadInteraction from '@ta/onboarding.explicit-preferences-external-tracking';
import SideBarProfile from './SideBarProfile';
import Logo from './Logo';
import styles from './SideBar.less';
import GlobalNavLinksV2 from './GlobalNavLinksV2';

type MemberType = {|
  avatar: string,
  name: string,
  firstName: string,
  lastName: string,
  email: string,
  profile: string,
  isOwner: boolean,
|};

type Props = {|
  /** Whether or not a jewel should be present on the hamburger menu. True if there are unread messages in the user's inbox */
  displayJewel: boolean,
  /** The current servlet name, used here for tracking purposes */
  servletName: string,
  /** The current page name, sometimes servletName is different from pageName */
  pageName: string,
  /** currentLocale of the page. */
  currentLocale: string,
  /** Data pertaining to the user if they're logged in. */
  member: ?MemberType,
  /** Current location id of the page. */
  locationId: number,
|};

/**
 * The data state is used for render media uploader modal on the pages when user clicks the media buttons under 'Post'.
 * We should render those modal outside SlideInOverlay so we put it here.
 * We pass these into MobileMediaActionsBlock and set these state their, then we will know what modal to render.
 */
type State = {|
  /** The modalname of the modal the need to be rendered. */
  uploaderModalName: string,
  /** The pid of the modal that need to be passed into the modal. */
  modalProductId: number,
  /** To see if we need to render the LinkUploader from the link of video uploader. */
  showLinkUploader: boolean,
  /** This is used to keep track of current sidebar sub links to go back to upper level */
  linksChain: Array<NavLinkType[]>,
|};

type SetUpExplicitPreferencesProps = {|
  children: (openEP: (boolean) => boolean) => React.Node,
|};

const SetUpExplicitPreferences = ({ children }: SetUpExplicitPreferencesProps) => {
  const {
    open: openExplicitPreferences,
    isEnabled: explicitPreferencesEnabled,
  } = useExplicitPreferencesModal('ONBOARDING');
  const { trackWouldOpenExplicitPreferences } = useExplicitPreferencesPreLoadInteraction(
    'ONBOARDING',
    'JOIN_BUTTON',
  );
  const tryToOpenExplicitPreferences = (hasCreatedMember: boolean) => {
    if (hasCreatedMember) {
      trackWouldOpenExplicitPreferences();
    }
    // Open the explicit preferences onboarding flow if it's enabled
    if (hasCreatedMember && explicitPreferencesEnabled) {
      openExplicitPreferences();
      return true;
    }

    return false;
  };

  return children(tryToOpenExplicitPreferences);
};

/** This is the component that renders the hamburger menu and triggers a slide in overlay */
class SideBar extends React.Component<Props, State> {
  state = {
    uploaderModalName: '',
    modalProductId: -1,
    showLinkUploader: false,
    linksChain: [],
  };
  /** This function is used to set Uploader Modal's modalname and pid which are used to show coresponding uploader modal  */
  setModalInfo = (name: string, pid: number) => {
    this.setState({ uploaderModalName: name, modalProductId: pid });
  };
  /** This function is used to set Link Uploader Modal when user click the Link Uploader link from Post Video under global nav bar  */
  setLinkUploader = (value: boolean) => {
    this.setState({ showLinkUploader: value });
  };

  /** This function will fire a tracking event whenever the user closes the sidebar by hitting the 'X' button  */
  trackOnCloseEvent = (): void => {
    const trackingProps: TrackingEventAJAXProps = {
      module: `TopNav_${this.props.servletName}`,
      action: 'close',
      context: 'sideBar',
    };
    trackEventAJAX(trackingProps);
  };

  /** This function will enable user to the parent links of current link items */
  backToParentLinks = (e: SyntheticEvent<*>) => {
    e.preventDefault();
    e.stopPropagation();

    // copy the link chain and remove the first one state
    const newLinksChain = this.state.linksChain.slice();
    newLinksChain.shift();
    this.setState({
      linksChain: newLinksChain,
    });
  };

  /** This function will render the selected sub links */
  goToSubLinks = (subLinks: NavLinkType[], e: SyntheticEvent<*>) => {
    e.preventDefault();
    e.stopPropagation();

    // copy the link chain and add the sub links
    const newLinksChain = this.state.linksChain.slice();
    newLinksChain.unshift(subLinks);
    this.setState({
      linksChain: newLinksChain,
    });
  };

  render() {
    const { uploaderModalName, modalProductId, showLinkUploader, linksChain } = this.state;
    const { servletName, pageName, currentLocale, member, locationId } = this.props;
    const sideBarClass = classNames(styles.sideBarContainer, 'ui_container');
    const moduleName = `TopNav_${servletName}`;
    const jewelClass = classNames('ui_jewel', styles.jewel);
    const jewel = this.props.displayJewel ? <div className={jewelClass} /> : null;
    const sideBarLinks = tryExplicitPreferences =>
      linksChain.length > 0 ? (
        <React.Fragment>
          <div onClick={e => this.backToParentLinks(e)}>
            <Icon name="single-chevron-left" />
            {/* Back */}
            {localize('mobile_back_8e0')}
          </div>
          <VisibleLinks links={linksChain[0]} expandLinks={this.goToSubLinks} />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <SideBarProfile
            displayInboxIcon={this.props.displayJewel}
            setModalInfo={this.setModalInfo}
            openExplicitPreferences={tryExplicitPreferences}
          />
          {featureIsEnabled('global_nav_links_test') ? (
            <GlobalNavLinksV2 isSidebar sidebarItemExpandHandler={this.goToSubLinks} />
          ) : (
            <SideBarNavLinksBinding />
          )}
        </React.Fragment>
      );

    return (
      <React.Fragment>
        {setUploaderModal(
          uploaderModalName,
          modalProductId,
          currentLocale,
          member,
          locationId,
          this.setLinkUploader,
          pageName,
        )}
        {showLinkUploaderFromVideoUploader(
          showLinkUploader,
          currentLocale,
          member,
          locationId,
          pageName,
        )}
        <Opener name="headerHamburgerMenu">
          {open => (
            <div onClick={open}>
              <ClickTracker action="open" module={moduleName} context="sidebar">
                <Icon name="menu-bars" />
                {jewel}
              </ClickTracker>
            </div>
          )}
        </Opener>
        <SetUpExplicitPreferences>
          {tryExplicitPreferences => (
            <SlideInOverlay
              name="headerHamburgerMenu"
              slideFrom="left"
              onClose={this.trackOnCloseEvent}
              header={
                <div className="ui_container">
                  <Logo />
                </div>
              }
            >
              {() => <div className={sideBarClass}>{sideBarLinks(tryExplicitPreferences)}</div>}
            </SlideInOverlay>
          )}
        </SetUpExplicitPreferences>
      </React.Fragment>
    );
  }
}

/** Function to get the parameters for the Inbox api call */
const getInboxParams = state => {
  const isMember = state.auth.isMember ? 'true' : 'false';

  const paramMap = {
    isMember, // Not used by endpoint but used to cachebust if user logs in.
  };

  return new URLSearchParams(paramMap);
};

/** Stage components for api calls and see if there are any unread messages. If so we'll need to display the jewel on the hamburger icon */
export default bind<Props, {||}, _, _, _, _>((state, stage) => {
  SideBarProfile.stage(state, stage);
  MobileMediaActionsBlock.stage(state, stage);
  PostButtonCollapsedMenu.stage(state, stage);
  SideBarNavLinksBinding.stage(state, stage);

  let member = null;
  const { loggedInUserId, isMember } = state.auth;
  // We don't need the member endpoint if logged out.
  if (isMember) {
    member = stage(`/data/1.0/member/self?userId=${loggedInUserId}`);
  }
  const inboxApiCall = stage(`/data/1.0/inbox/navAction?${getInboxParams(state).toString()}`);

  return newState => {
    const response = inboxApiCall.get().data || {};
    return {
      displayJewel: !newState.meta.readOnlyMode && response.hasUnreadConvos,
      servletName: newState.meta.initialServletName,
      pageName: newState.route.page,
      currentLocale: newState.i18n.locale,
      member: member ? member.get().data : null,
      locationId: newState.route.detail || 0,
    };
  };
})(SideBar);
